import React, { useState, useEffect } from 'react';
import UsersFilters from './UsersFilters';
import UsersTable from './UsersTable';
import ApiService from '../../../helpers/apiService';
import { Alert, Badge, Card, Pagination } from 'react-bootstrap';

interface FilterType {
  search: string;
  status?: { value: string; label: string } | undefined;
}

const Users: React.FC = () => {
  const [filters, setFilters] = useState<FilterType>({ search: '', status: undefined });
  const [users, setUsers] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [availableStatuses, setAvailableStatuses] = useState<{ value: string; label: string }[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, countAll: 0 });

  const fetchUsers = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await ApiService.get<{ data: any[]; total: number; statuses?: string[] }>(
        '/user/v1/list',
        { params: { search: filters.search, status: filters.status?.value, page: pagination.page, size: pagination.limit } }
      );
      setUsers(data.data ?? []);
      setPagination(prev => ({ ...prev, countAll: data.total ?? 0 }));
      if (data.statuses) setAvailableStatuses(data.statuses.map(s => ({ value: s, label: s })));
    } catch (error: any) {
      console.error('Ошибка при загрузке пользователей:', error);
      setUsers([]);
      const msg = error?.response?.data?.message ?? error?.message ?? '';
      const isServiceUnavailable = error?.response?.status === 500 && /refused|user service|rpc error/i.test(String(msg));
      setLoadError(isServiceUnavailable
        ? 'Сервис пользователей временно недоступен.'
        : msg || 'Не удалось загрузить список пользователей.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = () => setPagination(prev => ({ ...prev, page: 1, countAll: 0 }));

  const handlePageChange = (pageNumber: number) => {
    const safePage = Math.max(1, Math.min(pageNumber, totalPages || 1));
    setPagination(prev => ({ ...prev, page: safePage }));
  };

  const handleLimitChange = (newLimit: number) => setPagination(prev => ({ ...prev, limit: newLimit, page: 1, countAll: 0 }));

  useEffect(() => { fetchUsers(); }, [pagination.page, pagination.limit, filters]);

  const totalPages = Math.ceil(pagination.countAll / pagination.limit);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const maxVisible = 5;
    let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) { end = totalPages; start = Math.max(1, end - maxVisible + 1); }
    const items = [];
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item key={n} active={n === pagination.page} onClick={() => handlePageChange(n)}>{n}</Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-2 px-1">
        <span className="text-muted small">
          Всего: <strong>{pagination.countAll}</strong> · Страница <strong>{pagination.page}</strong> из <strong>{totalPages}</strong>
        </span>
        <Pagination className="mb-0" size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={pagination.page === 1} />
          <Pagination.Prev onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} />
          {items}
          <Pagination.Next onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={pagination.page >= totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">Пользователи</h3>
              <p className="mt-1 mb-0 text-muted small">Список зарегистрированных пользователей</p>
            </div>
            <Badge bg="secondary" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
              Всего: {pagination.countAll}
            </Badge>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <UsersFilters
            values={filters}
            setFieldValue={(field, value) => setFilters(prev => ({ ...prev, [field]: value }))}
            handleSubmit={handleFilterSubmit}
            loading={loading}
            statusOptions={availableStatuses}
          />

          {loadError && (
            <Alert variant="warning" className="py-2 small" dismissible onClose={() => setLoadError(null)}>
              {loadError}
            </Alert>
          )}

          <UsersTable users={users} fetchUsers={fetchUsers} limit={pagination.limit} onLimitChange={handleLimitChange} />
          {renderPagination()}
        </Card.Body>
      </Card>
    </>
  );
};

export default Users;
